import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { FiLoader } from "react-icons/fi";
import { MdCloudUpload, MdPictureAsPdf, MdDelete } from "react-icons/md";
import { useLockBodyScroll } from "../../../Hooks/useLockBodyScroll";
import { s3Upload } from "../../../../Utils/s3Upload";

/* ── FloatInput (matches AddressModal style) ── */
const FloatInput = ({ label, value, onChange, maxLength, required, type = "text" }) => {
  const [focused, setFocused] = useState(false);
  const active = focused || (value && value.length > 0);

  return (
    <div className="relative">
      <label
        className={`absolute left-3 transition-all duration-200 pointer-events-none z-10 bg-white dark:bg-darkModeSecondaryBgColor px-1
          ${active ? "-top-2 text-[10px] text-primaryColor font-medium" : "top-3 text-xs text-gray-400"}`}
      >
        {label}
        {required && <span className="text-primaryColor ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        placeholder=""
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full px-3 py-3 border-2 rounded-xl outline-none text-sm text-gray-800 dark:text-darkModeTextColor bg-white dark:bg-darkModeSecondaryBgColor
          transition-all duration-200
          ${focused ? "border-primaryColor shadow-sm shadow-primaryColor/20" : "border-gray-200 dark:border-gray-600 hover:border-gray-300"}`}
      />
    </div>
  );
};

// 🔧 Set these in your .env
const ADMIN_WHATSAPP_NUMBER = process.env.REACT_APP_ADMIN_WHATSAPP_NUMBER; // e.g. "919876543210"
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL; // e.g. "hr@company.com"

const CareerApplyModal = ({ career, onClose }) => {
  const [animate, setAnimate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(null); // 'whatsapp' | 'gmail' | null
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  useLockBodyScroll(!!career);

  useEffect(() => {
    if (career) setTimeout(() => setAnimate(true), 10);
  }, [career]);

  useEffect(() => {
    if (career) {
      setForm({ name: "", phone: "", email: "" });
      setResumeFile(null);
      setResumeUrl(null);
    }
  }, [career]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleResumeSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResumeFile(file);
    try {
      setUploading(true);
      const uploaded = await s3Upload(file, "careers");
      setResumeUrl(uploaded.url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Resume upload failed. Please try again.");
      setResumeFile(null);
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return false;
    }
    if (!form.phone.trim() || form.phone.trim().length < 10) {
      toast.error("Please enter a valid phone number.");
      return false;
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    if (!resumeUrl) {
      toast.error("Please upload your resume.");
      return false;
    }
    return true;
  };

  const buildMessage = () => {
    return (
      `📋 New Job Application\n\n` +
      `👤 Name: ${form.name}\n` +
      `📱 Phone: ${form.phone}\n` +
      `📧 Email: ${form.email}\n\n` +
      `💼 Position: ${career.title}\n` +
      (career.department ? `🏢 Department: ${career.department}\n` : "") +
      (career.location ? `📍 Location: ${career.location}\n` : "") +
      `\n📎 Resume: ${resumeUrl}\n\n` +
      `Please review and get back to the candidate.`
    );
  };

  const handleSend = (channel) => {
    if (!validate()) return;
    setSending(channel);

    const message = buildMessage();

    if (channel === "whatsapp") {
      const url = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    } else if (channel === "gmail") {
      const subject = `Job Application: ${career.title} - ${form.name}`;
      const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
        ADMIN_EMAIL
      )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }

    toast.success("Application ready to send!");
    setSending(null);
  };

  if (!career) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white dark:bg-darkModeSecondaryBgColor w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center rounded-t-3xl">
          <button onClick={handleClose} className="text-white text-2xl leading-none">
            ←
          </button>
          <div className="flex-1 text-center px-2">
            <p className="font-semibold text-lg">Apply Now</p>
            <p className="text-[11px] text-white/80 truncate">{career.title}</p>
          </div>
          <span className="w-6" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-4">
          <FloatInput
            label="Full Name"
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <FloatInput
            label="Phone Number"
            required
            maxLength={15}
            value={form.phone}
            onChange={(e) =>
              setForm((p) => ({ ...p, phone: e.target.value.replace(/[^\d+]/g, "") }))
            }
          />
          <FloatInput
            label="Email Address"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />

          {/* Resume upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-darkModeSecondaryTextColor mb-1.5 uppercase tracking-wide">
              Resume <span className="text-primaryColor">*</span>
            </label>

            <div className="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl p-4">
              {resumeFile ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <MdPictureAsPdf className="text-primaryColor flex-shrink-0" size={22} />
                    <span className="text-xs text-gray-600 dark:text-darkModeTextColor truncate">
                      {resumeFile.name}
                    </span>
                    {uploading && (
                      <FiLoader className="animate-spin text-primaryColor flex-shrink-0" size={14} />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setResumeFile(null);
                      setResumeUrl(null);
                    }}
                    className="text-red-500 flex-shrink-0"
                  >
                    <MdDelete size={18} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-20 cursor-pointer gap-1.5">
                  <MdCloudUpload size={24} className="text-gray-300 dark:text-darkModeSecondaryTextColor" />
                  <span className="text-xs text-gray-400 dark:text-darkModeSecondaryTextColor">
                    Click to upload resume (PDF/DOC)
                  </span>
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeSelect}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t dark:border-gray-700 flex gap-3">
          <button
            type="button"
            disabled={uploading || sending === "whatsapp"}
            onClick={() => handleSend("whatsapp")}
            className="flex-1 py-3.5 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-semibold transition disabled:opacity-60"
          >
            {sending === "whatsapp" && <FiLoader className="animate-spin" size={16} />}
            WhatsApp
          </button>
          <button
            type="button"
            disabled={uploading || sending === "gmail"}
            onClick={() => handleSend("gmail")}
            className="flex-1 py-3.5 flex items-center justify-center gap-2 bg-primaryColor text-white rounded-2xl font-semibold transition disabled:opacity-60"
          >
            {sending === "gmail" && <FiLoader className="animate-spin" size={16} />}
            Gmail
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CareerApplyModal;